import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useEffect } from 'react'
import { DRAG_TYPES } from '../constants'
import type { Stage } from '../types'

/**
 * Configuration for the useDragMonitoring hook
 */
export interface UseDragMonitoringConfig {
	/** Set of currently selected candidate IDs */
	selectedIds: Set<string>
	/** Available stages in the Kanban board */
	stages: Stage[]
	/** Maps each candidate ID to its current stage ID (null for orphaned candidates) */
	candidateStages: Map<string, string | null>
	/** Callback when candidate(s) are moved to a new stage */
	onCandidateMove: (candidateIds: string[], targetStage: string) => void
	/** Callback when stages are reordered (optional) */
	onStagesChange?: (stages: Stage[]) => void
	/** Callback when a candidate drag starts */
	onCandidateDragStart: (candidateId: string) => void
	/** Callback when a candidate drag ends */
	onCandidateDragEnd: () => void
	/** Callback when a column drag starts */
	onColumnDragStart: (columnId: string) => void
	/** Callback when a column drag ends */
	onColumnDragEnd: () => void
	/** Callback to clear selection (used after multi-select move) */
	onClearSelection: () => void
	/** Callback when a multi-select candidate drag starts (for analytics) */
	onMultiSelectDragStart?: (candidateCount: number) => void
}

/**
 * useDragMonitoring Hook
 *
 * A custom hook that sets up global drag-and-drop monitoring for the Kanban board.
 * Handles both candidate dragging (between columns) and column reordering.
 *
 * Features:
 * - Monitors all drag-and-drop operations in the board
 * - Handles candidate movement between stages
 * - Handles column reordering with proper edge detection
 * - Supports multi-select candidate operations
 * - Updates stage order properties after reordering
 *
 * The hook manages two types of drag operations:
 * 1. Candidate dragging - Moving candidates between stages
 * 2. Column dragging - Reordering the stage columns themselves
 *
 * @param config - Configuration object with callbacks and state
 *
 * @example
 * ```tsx
 * useDragMonitoring({
 *   selectedIds,
 *   stages,
 *   onCandidateMove: handleMove,
 *   onStagesChange: handleStagesChange,
 *   onCandidateDragStart: setDraggingId,
 *   onCandidateDragEnd: () => setDraggingId(null),
 *   onColumnDragStart: setDraggingColumnId,
 *   onColumnDragEnd: () => setDraggingColumnId(null),
 *   onClearSelection: () => setSelectedIds(new Set()),
 * });
 * ```
 */
export function useDragMonitoring({
	selectedIds,
	stages,
	candidateStages,
	onCandidateMove,
	onStagesChange,
	onCandidateDragStart,
	onCandidateDragEnd,
	onColumnDragStart,
	onColumnDragEnd,
	onClearSelection,
	onMultiSelectDragStart,
}: UseDragMonitoringConfig) {
	useEffect(() => {
		return monitorForElements({
			onDragStart: ({ source }) => {
				const data = source.data

				// Handle candidate drag start
				if (data.type === DRAG_TYPES.CANDIDATE && typeof data.candidateId === 'string') {
					onCandidateDragStart(data.candidateId)
					if (selectedIds.has(data.candidateId) && selectedIds.size > 1) {
						onMultiSelectDragStart?.(selectedIds.size)
					}
				} // Handle column drag start
				else if (data.type === DRAG_TYPES.COLUMN && typeof data.columnId === 'string') {
					onColumnDragStart(data.columnId)
				}
			},

			onDrop: ({ source, location }) => {
				const sourceData = source.data
				const dropTarget = location.current.dropTargets[0]

				// No valid drop target found
				if (!dropTarget) {
					onCandidateDragEnd()
					onColumnDragEnd()
					return
				}

				// Handle column reordering
				if (sourceData.type === DRAG_TYPES.COLUMN && dropTarget.data.type === DRAG_TYPES.COLUMN) {
					const draggedColumnId = sourceData.columnId as string
					const targetColumnId = dropTarget.data.columnId as string
					const closestEdge = extractClosestEdge(dropTarget.data)

					if (draggedColumnId !== targetColumnId && onStagesChange) {
						const draggedIndex = stages.findIndex((s) => s.id === draggedColumnId)
						const targetIndex = stages.findIndex((s) => s.id === targetColumnId)

						const newStages = [...stages]
						const [draggedStage] = newStages.splice(draggedIndex, 1)

						// Calculate insertion index based on which edge was targeted
						let insertIndex = targetIndex
						if (closestEdge === 'right') {
							// Dropping on the right edge means insert after the target
							insertIndex = draggedIndex < targetIndex ? targetIndex : targetIndex + 1
						} else {
							// Dropping on the left edge means insert before the target
							insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
						}

						newStages.splice(insertIndex, 0, draggedStage)

						// Update the order property for all stages
						const reorderedStages = newStages.map((stage, index) => ({
							...stage,
							order: index,
						}))

						onStagesChange(reorderedStages)
					}

					onColumnDragEnd()
					return
				}

				// Handle candidate movement
				if (sourceData.type === DRAG_TYPES.CANDIDATE) {
					const targetStage = dropTarget.data.stageId as string
					const draggedId = sourceData.candidateId as string
					const isBulkMove = selectedIds.has(draggedId)

					// A bulk move carries the whole selection (which may span several
					// stages); a single drag carries just the dragged card.
					const candidatesToMove = (isBulkMove ? Array.from(selectedIds) : [draggedId])
						// Drop candidates already in the target stage so we don't fire
						// redundant no-op moves (and so a bulk drop onto one of the source
						// stages still moves the candidates from the other stages).
						.filter(id => candidateStages.get(id) !== targetStage)

					if (candidatesToMove.length === 0) {
						onCandidateDragEnd()
						return
					}

					onCandidateMove(candidatesToMove, targetStage)
					if (isBulkMove) {
						onClearSelection()
					}
					onCandidateDragEnd()
				}
			},
		})
	}, [
		selectedIds,
		stages,
		candidateStages,
		onCandidateMove,
		onStagesChange,
		onCandidateDragStart,
		onCandidateDragEnd,
		onColumnDragStart,
		onColumnDragEnd,
		onClearSelection,
		onMultiSelectDragStart,
	])
}
